import { logger } from '../logger';
import { COMMON_EVENT_NAMES } from '@/utils/logger/instance/commonBusinessLogger';

/**
 * 将数值格式化为带千位分隔符和两位小数的字符串，不含货币符号。
 *
 * @param {number} amount - 要格式化的数值金额。
 * @returns {string} 格式化后的字符串，包含千位分隔符并固定为两位小数（例如："1,234.56"）。
 *
 * @example
 * formatPriceWithoutSymbol(1234.5);   // "1,234.50"
 * formatPriceWithoutSymbol(1234567);  // "1,234,567.00"
 */
export function formatPriceWithoutSymbol(amount: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * 将数值格式化为带千位分隔符和两位小数的字符串，不含货币符号。
 *
 * @param {number} n - 要格式化的数字。
 * @returns {string} 格式化后的字符串，包含千位分隔符（例如："11,234"）。
 *
 * @example
 * formatInteger(1234);   // "1,234"
 * formatInteger(1234567);  // "1,234,567"
 */
export function formatInteger(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}
/**
 * 货币格式化选项接口
 */
interface FormatCurrencyOptions {
  locale?: string;
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  currencyDisplay?: 'symbol' | 'code' | 'name'; // 单位显示方式
  errorIsShowAmount?: boolean; // 出现错误是否返回金额
}

/**
 * 货币格式化详细结果接口
 */
export interface CurrencyFormatResult {
  sign?: string; // 正负号（空字符串或 "-"）
  integerPart?: string; // 整数部分（带千分位）
  decimalPlace?: string; // 小数部分（若有）
  decimalSeparator?: string; // 小数点符号
  symbol?: string; // 货币单位符号
  symbolPosition?: 'before' | 'after'; // 单位位置（数字前/后）
  spacingPosition?: 'before-symbol' | 'after-symbol'; // 空格位置：在符号前还是符号后
  full: string; // 完整格式化字符串
  isError?: boolean;
  amount?: number;
}
// 货币符号清理函数：移除不需要的国家代码后缀
const cleanCurrencySymbol = (text: string, currency: string): string => {
  const cleanupPatterns = {
    GBP: { pattern: /£GB/g, replacement: '£' },
    USD: { pattern: /\$US/g, replacement: '$' },
    EUR: { pattern: /€EU/g, replacement: '€' },
  };

  const cleanup = cleanupPatterns[currency as keyof typeof cleanupPatterns];
  if (cleanup && text.match(cleanup.pattern)) {
    return text.replace(cleanup.pattern, cleanup.replacement);
  }
  return text;
};

// 检查货币符号前后是否有空格（优化版）
const getSpacingPosition = (
  fullString: string,
  unit: string,
): 'before-symbol' | 'after-symbol' | undefined => {
  // 无单位时直接返回undefined
  if (!unit) return undefined;

  const symbolIndex = fullString.indexOf(unit);
  // 单位不存在于完整字符串中（理论上不会发生，做防御处理）
  if (symbolIndex === -1) return undefined;

  // 检查符号前是否有空格（符号索引>0确保不会越界）
  // 检查普通空格和不间断空格（NBSP, \u00A0）
  if (symbolIndex > 0) {
    const charBefore = fullString[symbolIndex - 1];
    if (charBefore === ' ' || charBefore === '\u00A0') {
      return 'before-symbol';
    }
  }

  // 检查符号后是否有空格（计算符号结束位置，避免越界）
  const symbolEndIndex = symbolIndex + unit.length;
  if (symbolEndIndex < fullString.length) {
    const charAfter = fullString[symbolEndIndex];
    if (charAfter === ' ' || charAfter === '\u00A0') {
      return 'after-symbol';
    }
  }

  // 无空格
  return undefined;
};

/**
 * 格式化货币金额，支持多种本地化和货币类型
 *
 * @param amount - 要格式化的金额数字
 * @param options - 格式化选项
 * @returns 格式化后的货币字符串或详细对象
 *
 * @example
 * formatCurrency(1234.56, { locale: 'en-GB', currency: 'GBP' });
 * // "£1,234.56"
 *
 * formatCurrency(1234.56, { locale: 'fr-FR', currency: 'EUR' });
 * // { sign: '', integerPart: '1 234', decimalPlace: '56', ... }
 */

export function formatCurrency(
  amount: number,
  options: FormatCurrencyOptions,
): CurrencyFormatResult {
  const {
    locale,
    currency,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    currencyDisplay = 'symbol',
    errorIsShowAmount = true,
  } = options || {};

  try {
    // 规范化 locale：将下划线替换为连字符（处理类型安全）
    const formatLocale = typeof locale === 'string' ? locale.replace(/_/g, '-') : locale;
    if (!locale || !currency) {
      console.error('价格参数错误:', { ...options, amount });
      logger.info({
        eventName: COMMON_EVENT_NAMES.JDI_PRICE_PARAMS_ERROR,
        message: {
          amount,
          ...options,
        },
      });

      return { isError: true, full: errorIsShowAmount ? `${amount}` : '' };
    }
    // 创建格式化实例
    const formatter = new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
      currencyDisplay,
    });

    // 生成完整格式化字符串
    let fullString = formatter.format(amount);

    // 清理完整格式化字符串
    fullString = cleanCurrencySymbol(fullString, currency);

    // 使用 formatToParts 获取结构化的各部分（原生API，可靠无歧义）
    const parts = formatter.formatToParts(amount);

    // 初始化结果变量
    let sign = '';
    let integer = '';
    let fraction = '';
    let decimalSeparator = '';
    let unit = '';
    let unitPosition: 'before' | 'after' = 'before';
    let hasSeenNumber = false;

    // 解析各部分
    for (const part of parts) {
      switch (part.type) {
        case 'currency':
          unit = cleanCurrencySymbol(part.value, currency);
          // 判断单位位置：在数字前还是后
          unitPosition = hasSeenNumber ? 'after' : 'before';
          break;
        case 'minusSign':
          sign = part.value; // 负号
          break;
        case 'integer':
          integer += part.value; // 累加整数部分
          hasSeenNumber = true;
          break;
        case 'group':
          integer += part.value; // 添加千分位分隔符
          break;
        case 'fraction':
          fraction = part.value; // 小数部分
          hasSeenNumber = true;
          break;
        case 'decimal':
          decimalSeparator = part.value; // 小数点
          break;
        // 其他类型暂时忽略
      }
    }

    // 检查货币符号前后是否有空格
    const spacingPosition = getSpacingPosition(fullString, unit);

    // 构造并返回格式化结果对象
    return {
      sign,
      integerPart: integer,
      decimalPlace: fraction || '',
      decimalSeparator,
      symbol: unit,
      symbolPosition: unitPosition,
      spacingPosition, // 添加空格位置信息
      full: fullString,
    };
  } catch (error) {
    // 发生错误时返回回退格式
    console.error('formatCurrency error:', error);
    return { isError: true, full: errorIsShowAmount ? `${amount}` : '' };
  }
}
/**
 * 用于富文本内的货币格式化
 * 支持获取整数位、小数位、单位、单位位置等信息
 * @param price 金额数字
 * @param locale 格式化选项
 * @returns 完整字符串或包含各部分详细信息的对象
 */

export function calcSymbol(price: string, locale: string, currency: string) {
  const { symbolPosition, symbol, full } = formatCurrency(parseFloat(price), {
    locale,
    currency,
    currencyDisplay: 'symbol', // 强制使用符号而不是代码
  }) as {
    sign: string;
    integerPart: string;
    decimalPlace?: string;
    decimalSeparator: string;
    symbol: string;
    symbolPosition: 'before' | 'after';
    full: string;
  };
  return full;
}
